import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Calendar, X, Loader2, Send, Upload, ExternalLink } from 'lucide-react';
import api from '../lib/api';

const COURSE_FILTERS = ['All', 'Data Analytics', 'Business Analytics', 'Data Science'];
const MAX_RESUME_BYTES = 5 * 1024 * 1024;

export default function Jobs() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All');
    const [selectedJob, setSelectedJob] = useState(null); // job detail modal
    const [applyJob, setApplyJob] = useState(null); // apply modal

    const load = async () => {
        setLoading(true);
        try {
            const params = filter !== 'All' ? { course: filter } : {};
            const res = await api.get('/jobs', { params });
            setJobs(res.data || []);
        } catch (_) {
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return (
        <div className="space-y-6" data-testid="jobs-page">
            {/* Hero */}
            <section className="brand-hero-gradient rounded-2xl p-8 text-white">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                    <Briefcase className="h-3.5 w-3.5" /> JOB PORTAL
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-extrabold">Discover opportunities</h1>
                <p className="mt-2 text-white/90 text-sm md:text-base max-w-2xl">
                    Curated openings for ExcellenC learners. Apply directly with your resume in a few clicks.
                </p>
            </section>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                {COURSE_FILTERS.map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        data-testid={`filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`brand-chip ${filter === f ? 'brand-chip-active' : ''}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading jobs…
                </div>
            ) : jobs.length === 0 ? (
                <div className="brand-card p-10 text-center">
                    <div className="text-lg font-bold text-[#111827]">No jobs available yet</div>
                    <p className="mt-1 text-sm text-[#6B7280]">Check back later or ask your admin to post one.</p>
                </div>
            ) : (
                <div className="grid gap-5 md:grid-cols-2">
                    {jobs.map((j) => (
                        <JobCard
                            key={j.id}
                            job={j}
                            onView={() => setSelectedJob(j)}
                            onApply={() => setApplyJob(j)}
                        />
                    ))}
                </div>
            )}

            <AnimatePresence>
                {selectedJob && (
                    <JobDetailModal
                        job={selectedJob}
                        onClose={() => setSelectedJob(null)}
                        onApply={() => {
                            setApplyJob(selectedJob);
                            setSelectedJob(null);
                        }}
                    />
                )}
                {applyJob && (
                    <ApplyModal
                        job={applyJob}
                        onClose={() => setApplyJob(null)}
                        onSuccess={() => {
                            setApplyJob(null);
                            load();
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function JobCard({ job, onView, onApply }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="brand-card p-5 flex flex-col"
            data-testid={`job-card-${job.id}`}
        >
            <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C3A94] to-[#3552B8] text-white font-extrabold">
                    {job.company?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                    <button onClick={onView} className="text-left w-full">
                        <div className="text-base font-bold text-[#111827] leading-tight truncate">
                            {job.title}
                        </div>
                        <div className="mt-0.5 text-sm text-[#6B7280]">{job.company}</div>
                    </button>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        <span className="brand-chip">
                            <MapPin className="h-3 w-3" /> {job.location}
                        </span>
                        <span className="brand-chip">{job.job_type}</span>
                        <span className="brand-chip">{job.experience_level}</span>
                        <span className="brand-chip text-emerald-700 border-emerald-200 bg-emerald-50">
                            {job.salary_range}
                        </span>
                    </div>
                </div>
            </div>

            {job.course_tag && (
                <div className="mt-3">
                    <span className="inline-flex items-center rounded-full bg-[#F1F5FD] px-2.5 py-0.5 text-[11px] font-semibold text-[#1C3A94]">
                        {job.course_tag}
                    </span>
                </div>
            )}

            <p className="mt-3 text-sm text-[#6B7280] line-clamp-3">{job.description}</p>

            <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-[11px] text-[#9CA3AF]">
                    Posted {new Date(job.posted_at).toLocaleDateString()}
                </span>
                <div className="flex gap-2">
                    <button onClick={onView} className="brand-btn-ghost text-xs">
                        View
                    </button>
                    <button onClick={onApply} data-testid={`apply-btn-${job.id}`} className="brand-btn-primary text-xs">
                        Apply Now
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

function JobDetailModal({ job, onClose, onApply }) {
    return (
        <ModalShell onClose={onClose} testId="job-detail-modal">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold text-[#6B7280]">{job.company}</div>
                    <h2 className="mt-1 text-xl font-extrabold text-[#111827]">{job.title}</h2>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        <span className="brand-chip">
                            <MapPin className="h-3 w-3" /> {job.location}
                        </span>
                        <span className="brand-chip">{job.job_type}</span>
                        <span className="brand-chip">{job.experience_level}</span>
                        <span className="brand-chip text-emerald-700 border-emerald-200 bg-emerald-50">
                            {job.salary_range}
                        </span>
                    </div>
                </div>
                <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]">
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="mt-5 space-y-4 text-sm text-[#111827] leading-relaxed">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94] mb-1">Description</div>
                    <p className="whitespace-pre-line">{job.description}</p>
                </div>
                {job.requirements?.length > 0 && (
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94] mb-1">Requirements</div>
                        <ul className="list-disc pl-5 space-y-1">
                            {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                    </div>
                )}
                {job.skills_required?.length > 0 && (
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94] mb-1">Skills</div>
                        <div className="flex flex-wrap gap-1.5">
                            {job.skills_required.map((s) => <span key={s} className="brand-chip">{s}</span>)}
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3 pt-2 text-xs text-[#6B7280]">
                    {job.application_deadline && (
                        <div className="inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                        </div>
                    )}
                    {job.apply_link && (
                        <a
                            href={job.apply_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[#1C3A94] hover:underline"
                        >
                            <ExternalLink className="h-3.5 w-3.5" /> External link
                        </a>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <button onClick={onClose} className="brand-btn-outline">Close</button>
                <button onClick={onApply} data-testid="detail-apply-btn" className="brand-btn-primary">
                    Apply Now
                </button>
            </div>
        </ModalShell>
    );
}

function ApplyModal({ job, onClose, onSuccess }) {
    const [form, setForm] = useState({
        full_name: '',
        contact_number: '',
        email: '',
        learner_id: '',
        linkedin_url: '',
    });
    const [resumeMeta, setResumeMeta] = useState(null); // { filename, mimetype, base64, size }
    const [err, setErr] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onFile = (e) => {
        const file = e.target.files?.[0];
        setErr('');
        if (!file) return;
        if (file.size > MAX_RESUME_BYTES) {
            setErr('Resume must be under 5MB');
            return;
        }
        const okTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];
        if (!okTypes.includes(file.type) && !/\.(pdf|doc|docx)$/i.test(file.name)) {
            setErr('Only PDF, DOC, or DOCX allowed');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const b64 = String(reader.result || '').split(',')[1] || '';
            setResumeMeta({ filename: file.name, mimetype: file.type || 'application/octet-stream', base64: b64, size: file.size });
        };
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!resumeMeta) {
            setErr('Please upload your resume');
            return;
        }
        if (!/linkedin\.com/i.test(form.linkedin_url)) {
            setErr('LinkedIn URL must contain linkedin.com');
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/jobs/${job.id}/apply`, {
                ...form,
                resume_base64: resumeMeta.base64,
                resume_filename: resumeMeta.filename,
                resume_mimetype: resumeMeta.mimetype,
            });
            alert('Application submitted successfully!'); // eslint-disable-line no-alert
            onSuccess();
        } catch (e2) {
            setErr(e2.response?.data?.detail || 'Failed to apply');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalShell onClose={onClose} testId="apply-modal">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold text-[#6B7280]">{job.company}</div>
                    <h2 className="mt-1 text-xl font-extrabold text-[#111827]">Apply for {job.title}</h2>
                </div>
                <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]"><X className="h-5 w-5" /></button>
            </div>

            {err && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {err}
                </div>
            )}

            <form onSubmit={submit} className="mt-5 grid gap-4 md:grid-cols-2" data-testid="apply-form">
                <FormField label="Full name *">
                    <input data-testid="apply-full-name" required className="brand-input" value={form.full_name} onChange={set('full_name')} />
                </FormField>
                <FormField label="Contact number *">
                    <input data-testid="apply-contact" required pattern="[0-9+\-\s]{7,15}" className="brand-input" value={form.contact_number} onChange={set('contact_number')} />
                </FormField>
                <FormField label="Email *">
                    <input data-testid="apply-email" required type="email" className="brand-input" value={form.email} onChange={set('email')} />
                </FormField>
                <FormField label="ExcellenC Learner ID *">
                    <input data-testid="apply-learner-id" required className="brand-input" value={form.learner_id} onChange={set('learner_id')} />
                </FormField>
                <FormField label="LinkedIn URL *" full>
                    <input data-testid="apply-linkedin" required className="brand-input" placeholder="https://linkedin.com/in/yourname" value={form.linkedin_url} onChange={set('linkedin_url')} />
                </FormField>
                <FormField label="Resume (PDF/DOC/DOCX, max 5MB) *" full>
                    <div className="flex items-center gap-3">
                        <label className="brand-btn-outline gap-1.5 cursor-pointer" data-testid="resume-upload-btn">
                            <Upload className="h-4 w-4" />
                            <span>Choose file</span>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                className="hidden"
                                onChange={onFile}
                                data-testid="resume-input"
                            />
                        </label>
                        {resumeMeta && (
                            <div className="text-xs text-[#6B7280] truncate">
                                {resumeMeta.filename} ({(resumeMeta.size / 1024).toFixed(1)} KB)
                            </div>
                        )}
                    </div>
                </FormField>

                <div className="md:col-span-2 mt-2 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="brand-btn-outline">Cancel</button>
                    <button type="submit" data-testid="submit-application-btn" disabled={submitting} className="brand-btn-primary gap-2">
                        <Send className="h-4 w-4" /> {submitting ? 'Submitting…' : 'Submit application'}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function FormField({ label, children, full }) {
    return (
        <div className={full ? 'md:col-span-2' : ''}>
            <label className="mb-1 block text-xs font-semibold text-[#111827]">{label}</label>
            {children}
        </div>
    );
}

function ModalShell({ children, onClose, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
            data-testid={testId}
        >
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl brand-card p-6 md:p-8 max-h-[90vh] overflow-y-auto my-auto"
            >
                {children}
            </motion.div>
        </motion.div>
    );
}
